import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Package, FileText, LogOut, ShoppingBag, Clock, CheckCircle2, XCircle, Download } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const STATUS_META = {
  pending: { label: 'En attente', icon: Clock, cls: 'bg-amber-100 text-amber-800' },
  paid: { label: 'Payée', icon: CheckCircle2, cls: 'bg-emerald-100 text-emerald-800' },
  shipped: { label: 'Expédiée', icon: Package, cls: 'bg-blue-100 text-blue-800' },
  delivered: { label: 'Livrée', icon: CheckCircle2, cls: 'bg-emerald-100 text-emerald-800' },
  cancelled: { label: 'Annulée', icon: XCircle, cls: 'bg-red-100 text-red-800' },
  sent: { label: 'Envoyé', icon: CheckCircle2, cls: 'bg-blue-100 text-blue-800' },
  accepted: { label: 'Accepté', icon: CheckCircle2, cls: 'bg-emerald-100 text-emerald-800' },
  rejected: { label: 'Refusé', icon: XCircle, cls: 'bg-red-100 text-red-800' },
};

const StatusBadge = ({ status }) => {
  const meta = STATUS_META[status] || STATUS_META.pending;
  const Icon = meta.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1 rounded-full ${meta.cls}`}>
      <Icon className="w-3 h-3" />
      {meta.label}
    </span>
  );
};

export const MonCompte = () => {
  const { user, logout, authHeaders } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('orders');

  useEffect(() => {
    if (!user) return;
    Promise.all([
      axios.get(`${BACKEND}/api/orders/my`, { headers: authHeaders() }).catch(() => ({ data: [] })),
      axios.get(`${BACKEND}/api/quotes?email=${encodeURIComponent(user.email)}`).catch(() => ({ data: [] })),
    ])
      .then(([o, q]) => {
        setOrders(o.data || []);
        setQuotes(q.data || []);
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handleLogout = () => {
    logout();
    toast.success('Déconnecté');
    navigate('/');
  };

  const downloadQuote = async (quote, format) => {
    try {
      const r = await axios.get(`${BACKEND}/api/quotes/${quote.id}/download/${format}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url;
      const ext = format === 'word' ? 'docx' : format === 'excel' ? 'xlsx' : 'pdf';
      a.download = `devis_${quote.quote_number}.${ext}`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Téléchargement impossible');
    }
  };

  return (
    <div className="min-h-screen bg-muted/30 py-12 px-4">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="font-heading font-bold text-4xl text-secondary" data-testid="mon-compte-title">
              Bonjour, {user?.name}
            </h1>
            <p className="text-sm text-muted-foreground mt-1">{user?.email}</p>
          </div>
          <button
            onClick={handleLogout}
            className="inline-flex items-center gap-2 border border-border text-secondary hover:bg-white px-5 py-3 rounded-lg font-medium"
            data-testid="mon-compte-logout-btn"
          >
            <LogOut className="w-4 h-4" /> Déconnexion
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          <div className="bg-white border border-border rounded-xl p-6">
            <div className="flex items-center gap-3 mb-2">
              <ShoppingBag className="w-5 h-5 text-primary" />
              <span className="text-sm text-muted-foreground">Mes commandes</span>
            </div>
            <p className="text-3xl font-bold text-secondary" data-testid="stat-orders-count">{orders.length}</p>
          </div>
          <div className="bg-white border border-border rounded-xl p-6">
            <div className="flex items-center gap-3 mb-2">
              <FileText className="w-5 h-5 text-accent" />
              <span className="text-sm text-muted-foreground">Mes devis</span>
            </div>
            <p className="text-3xl font-bold text-secondary" data-testid="stat-quotes-count">{quotes.length}</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white border border-border rounded-xl overflow-hidden">
          <div className="border-b border-border flex">
            <button
              onClick={() => setTab('orders')}
              className={`px-6 py-4 font-medium text-sm transition-colors ${tab === 'orders' ? 'text-primary border-b-2 border-primary' : 'text-muted-foreground hover:text-secondary'}`}
              data-testid="tab-orders"
            >
              Commandes ({orders.length})
            </button>
            <button
              onClick={() => setTab('quotes')}
              className={`px-6 py-4 font-medium text-sm transition-colors ${tab === 'quotes' ? 'text-primary border-b-2 border-primary' : 'text-muted-foreground hover:text-secondary'}`}
              data-testid="tab-quotes"
            >
              Devis ({quotes.length})
            </button>
          </div>

          {loading ? (
            <div className="p-12 text-center text-muted-foreground">Chargement…</div>
          ) : tab === 'orders' ? (
            orders.length === 0 ? (
              <div className="p-12 text-center">
                <Package className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground mb-4">Aucune commande pour le moment.</p>
                <Link to="/produits" className="inline-flex items-center gap-2 bg-primary text-primary-foreground hover:bg-primary/90 px-5 py-2.5 rounded-lg font-medium">
                  Parcourir les produits
                </Link>
              </div>
            ) : (
              <div className="divide-y divide-border">
                {orders.map((o) => (
                  <div key={o.id} className="p-6 hover:bg-muted/20" data-testid={`order-row-${o.id}`}>
                    <div className="flex flex-wrap items-start justify-between gap-4 mb-3">
                      <div>
                        <p className="font-mono text-xs text-muted-foreground">Commande #{o.id.slice(0, 8)}</p>
                        <p className="text-sm text-muted-foreground mt-1">
                          {new Date(o.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' })}
                        </p>
                      </div>
                      <StatusBadge status={o.status} />
                    </div>
                    <div className="space-y-1 mb-3">
                      {o.items.map((it, i) => (
                        <div key={i} className="flex justify-between text-sm">
                          <span className="text-secondary">{it.name} × {it.quantity}</span>
                          <span className="text-muted-foreground">{it.subtotal.toFixed(2)} DH</span>
                        </div>
                      ))}
                    </div>
                    <div className="flex justify-between items-center pt-3 border-t border-border">
                      <span className="text-sm font-medium text-secondary">Total</span>
                      <span className="text-xl font-bold text-primary">{o.total.toFixed(2)} DH</span>
                    </div>
                  </div>
                ))}
              </div>
            )
          ) : quotes.length === 0 ? (
            <div className="p-12 text-center">
              <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground mb-4">Aucun devis demandé.</p>
              <Link to="/demander-devis" className="inline-flex items-center gap-2 bg-accent text-accent-foreground hover:bg-accent/90 px-5 py-2.5 rounded-lg font-medium">
                Demander un devis
              </Link>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {quotes.map((q) => (
                <div key={q.id} className="p-6 hover:bg-muted/20" data-testid={`quote-row-${q.id}`}>
                  <div className="flex flex-wrap items-start justify-between gap-4 mb-3">
                    <div>
                      <p className="font-semibold text-secondary">{q.quote_number}</p>
                      <p className="text-sm text-muted-foreground mt-1">
                        {new Date(q.created_at).toLocaleDateString('fr-FR')}
                      </p>
                    </div>
                    <StatusBadge status={q.status} />
                  </div>
                  <div className="flex flex-wrap items-center justify-between gap-3 pt-3 border-t border-border">
                    <span className="text-xl font-bold text-primary">{q.total.toFixed(2)} DH</span>
                    <div className="flex gap-2">
                      <button onClick={() => downloadQuote(q, 'pdf')} className="inline-flex items-center gap-1.5 text-sm border border-border hover:bg-muted px-3 py-1.5 rounded-lg">
                        <Download className="w-3 h-3" /> PDF
                      </button>
                      <button onClick={() => downloadQuote(q, 'word')} className="inline-flex items-center gap-1.5 text-sm border border-border hover:bg-muted px-3 py-1.5 rounded-lg">
                        <Download className="w-3 h-3" /> Word
                      </button>
                      <button onClick={() => downloadQuote(q, 'excel')} className="inline-flex items-center gap-1.5 text-sm border border-border hover:bg-muted px-3 py-1.5 rounded-lg">
                        <Download className="w-3 h-3" /> Excel
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
